import React, { useEffect, useMemo, useState } from "react";
import { Card, Col, Row, Form, Button } from "react-bootstrap";
import { toast } from "react-toastify";
import axios from "axios";

/*
Assumptions:
- localStorage.facilitiesData = [{ key, value, groupName? | group? | program? }]
- /users/list-notifications -> [{ id, notification_name }]
- /users/list-user-notifications -> [{ facility_id, notification_id }]
- /users/optins-notifications accepts { notifications: [{ facility_id, notification_id, is_active }] }
*/

const UserPharmacyNotification = () => {
  const [typeOptions, setTypeOptions] = useState([]);
  const [selectedType, setSelectedType] = useState("");
  const groupData = JSON.parse(localStorage.getItem("groupsData") || "[]");
  const facilityList = JSON.parse(localStorage.getItem("facilitiesData") || "[]");

  // Build groups from facilities
  const groupOptions = useMemo(() => {
    const map = new Map();
    groupData.forEach(f => {
      if (!map.has(f.key)) map.set(f.value, f.key);
    });
    // Sort groups alphabetically by label
    return [...map.entries()]
      .map(([value, label]) => ({ value, label }))
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [facilityList]);

  const [selectedGroup, setSelectedGroup] = useState(
    groupOptions.length ? groupOptions[0]?.value : ""
  );

  // Maps
  const [facilityToNotifications, setFacilityToNotifications] = useState({});   // facility_id -> [notification_id]
  const [notificationToFacilities, setNotificationToFacilities] = useState({}); // notification_id -> [facility_id]

  // Current selection for the chosen type (all facilities across groups)
  const [selectedFacilitiesForType, setSelectedFacilitiesForType] = useState([]);

  // Dual-listbox UI selections (only within the current group view)
  const [leftSelected, setLeftSelected] = useState([]);   // available in group
  const [rightSelected, setRightSelected] = useState([]); // selected in group

  useEffect(() => {
    // Initial fetches
    fetchNotificationTypes();
    fetchUserFacilityNotificationMap();
  }, []);

  useEffect(() => {
    // When groups load, set a default selected group
    if (!selectedGroup && groupOptions.length) {
      setSelectedGroup(groupOptions[0].value);
    }
  }, [groupOptions, selectedGroup]);

  useEffect(() => {
    // When notification type changes, sync the selected list for that type
    if (!selectedType) {
      setSelectedFacilitiesForType([]);
      return;
    }
    const preselected = (notificationToFacilities[selectedType] || []).map(String);
    setSelectedFacilitiesForType(preselected);
    // Clear dual-listbox in-group selections
    setLeftSelected([]);
    setRightSelected([]);
  }, [selectedType, notificationToFacilities]);

  useEffect(() => {
    // When group changes, clear in-group UI selections
    setLeftSelected([]);
    setRightSelected([]);
  }, [selectedGroup]);

  async function fetchNotificationTypes() {
    const tokenStr = localStorage.getItem("accessToken");
    const url = `${process.env.REACT_APP_SERVER_URL}/users/list-notifications`;
    const { data } = await axios.get(url, {
      headers: { Authorization: `Bearer ${tokenStr}` }
    });
    const list = [{ key: "Select Type", value: "" }].concat(
      (data?.data || []).map(n => ({
        key: n.notification_name,
        value: String(n.id)
      }))
    );
    setTypeOptions(list);
  }

  async function fetchUserFacilityNotificationMap() {
    const tokenStr = localStorage.getItem("accessToken");
    const url = `${process.env.REACT_APP_SERVER_URL}/users/list-user-notifications`;
    const { data } = await axios.get(url, {
      headers: { Authorization: `Bearer ${tokenStr}` }
    });

    const f2n = {};
    const n2f = {};
    for (const item of data?.data || []) {
      const fid = String(item.facility_id);
      const nid = String(item.notification_id);
      if (!f2n[fid]) f2n[fid] = [];
      if (!n2f[nid]) n2f[nid] = [];
      if (!f2n[fid].includes(nid)) f2n[fid].push(nid);
      if (!n2f[nid].includes(fid)) n2f[nid].push(fid);
    }
    setFacilityToNotifications(f2n);
    setNotificationToFacilities(n2f);
  }

  // Facilities filtered by current group
  const facilitiesInGroup = useMemo(() => {
    if (!selectedGroup) return [];
    return facilityList.filter(f => f.group_id == selectedGroup);
  }, [facilityList, selectedGroup]);

  // Split into Available (left) and Selected (right) for the current group
  const leftAvailable = useMemo(() => {
    const selectedSet = new Set(selectedFacilitiesForType);
    return facilitiesInGroup.filter(f => !selectedSet.has(String(f.value)));
  }, [facilitiesInGroup, selectedFacilitiesForType]);

  const rightChosen = useMemo(() => {
    const selectedSet = new Set(selectedFacilitiesForType);
    return facilitiesInGroup.filter(f => selectedSet.has(String(f.value)));
  }, [facilitiesInGroup, selectedFacilitiesForType]);

  // Dual-listbox handlers
  const moveRight = () => {
    if (!leftSelected.length) return;
    const addSet = new Set(selectedFacilitiesForType);
    leftSelected.forEach(fid => addSet.add(fid));
    setSelectedFacilitiesForType([...addSet]);
    setLeftSelected([]);
  };

  const moveLeft = () => {
    if (!rightSelected.length) return;
    const removeSet = new Set(selectedFacilitiesForType);
    rightSelected.forEach(fid => removeSet.delete(fid));
    setSelectedFacilitiesForType([...removeSet]);
    setRightSelected([]);
  };

  const moveAllRight = () => {
    const addSet = new Set(selectedFacilitiesForType);
    leftAvailable.forEach(f => addSet.add(String(f.value)));
    setSelectedFacilitiesForType([...addSet]);
    setLeftSelected([]);
  };

  const moveAllLeft = () => {
    const removeSet = new Set(selectedFacilitiesForType);
    rightChosen.forEach(f => removeSet.delete(String(f.value)));
    setSelectedFacilitiesForType([...removeSet]);
    setRightSelected([]);
  };

  const handleSave = async () => {
    if (!selectedType) {
      toast.error("Select a notification type.", { theme: "colored" });
      return;
    }
    const preselectedFacilities = (notificationToFacilities[selectedType] || []).map(String);
    const currentSelected = selectedFacilitiesForType.map(String);

    const toActivate = currentSelected
      .filter(fid => !preselectedFacilities.includes(fid))
      .map(fid => ({
        facility_id: parseInt(fid, 10),
        notification_id: parseInt(selectedType, 10),
        is_active: 1
      }));

    const toDeactivate = preselectedFacilities
      .filter(fid => !currentSelected.includes(fid))
      .map(fid => ({
        facility_id: parseInt(fid, 10),
        notification_id: parseInt(selectedType, 10),
        is_active: 0
      }));

    const notifications = [...toActivate, ...toDeactivate];
    if (notifications.length === 0) {
      toast.info("No changes to save.", { theme: "colored" });
      return;
    }

    const tokenStr = localStorage.getItem("accessToken");
    const url = `${process.env.REACT_APP_SERVER_URL}/users/optins-notifications`;
    const { data } = await axios.post(
      url,
      { notifications },
      { headers: { Authorization: `Bearer ${tokenStr}` } }
    );

    if (data?.status) {
      toast.success("Notification settings updated.", { theme: "colored" });
      await fetchUserFacilityNotificationMap();
    } else {
      toast.error("Failed to update notification settings.", { theme: "colored" });
    }
  };

  return (
    <>
      {typeOptions?.length > 0 ? (
        <Card className="mt-3">
          <Card.Body>
            <h4 style={{ marginBottom: 16 }}>Notification Settings</h4>
            <Row className="mb-3">
              <Col md={6} className="mb-3">
                <Form.Group>
                  <Form.Label className="required">Type</Form.Label>
                  <Form.Select
                    value={selectedType}
                    onChange={e => setSelectedType(e.target.value)}
                  >
                    {typeOptions.map(opt => (
                      <option key={opt.value} value={opt.value}>
                        {opt.key}
                      </option>
                    ))}
                  </Form.Select>
                </Form.Group>
              </Col>

              <Col md={6} className="mb-3">
                <Form.Group>
                  <Form.Label className="required">Group</Form.Label>
                  <Form.Select
                    value={selectedGroup}
                    onChange={e => setSelectedGroup(e.target.value)}
                  >
                    {groupOptions.map(g => (
                      <option key={g.value} value={g.value}>
                        {g.label}
                      </option>
                    ))}
                  </Form.Select>
                </Form.Group>
              </Col>
            </Row>
            <Row className="align-items-center">
              <Col md={5}>
                <Form.Group>
                  <Form.Label style={{ fontWeight: 600 }}>
                    Available facilities in this group
                  </Form.Label>
                  <Form.Control
                    as="select"
                    multiple
                    value={leftSelected}
                    onChange={e =>
                      setLeftSelected([...e.target.selectedOptions].map(o => o.value))
                    }
                    style={{ height: 280, fontSize: 14 }}
                  >
                    {leftAvailable.map(f => (
                      <option key={f.value} value={f.value}>
                        {f.key}
                      </option>
                    ))}
                  </Form.Control>
                </Form.Group>
              </Col>

              <Col
                md={2}
                className="d-flex flex-column justify-content-center align-items-center"
              >
                <Button
                  variant="primary"
                  className="mb-2"
                  onClick={moveRight}
                  style={{ width: 56 }}
                  disabled={!leftSelected.length}
                  title="Add selected"
                >
                  &gt;
                </Button>
                <Button
                  variant="primary"
                  className="mb-2"
                  onClick={moveAllRight}
                  style={{ width: 56 }}
                  disabled={!leftAvailable.length}
                  title="Add all"
                >
                  &gt;&gt;
                </Button>
                <Button
                  variant="secondary"
                  className="mb-2"
                  onClick={moveLeft}
                  style={{ width: 56 }}
                  disabled={!rightSelected.length}
                  title="Remove selected"
                >
                  &lt;
                </Button>
                <Button
                  variant="secondary"
                  onClick={moveAllLeft}
                  style={{ width: 56 }}
                  disabled={!rightChosen.length}
                  title="Remove all"
                >
                  &lt;&lt;
                </Button>
              </Col>

              <Col md={5}>
                <Form.Group>
                  <Form.Label style={{ fontWeight: 600 }}>
                    Selected facilities for this type (in this group)
                  </Form.Label>
                  <Form.Control
                    as="select"
                    multiple
                    value={rightSelected}
                    onChange={e =>
                      setRightSelected([...e.target.selectedOptions].map(o => o.value))
                    }
                    style={{ height: 280, fontSize: 14 }}
                  >
                    {rightChosen.map(f => (
                      <option key={f.value} value={f.value}>
                        {f.key}
                      </option>
                    ))}
                  </Form.Control>
                </Form.Group>
              </Col>
            </Row>

            <Row className="mt-3">
              <Col className="d-flex justify-content-center">
                <Button
                  variant="primary"
                  size="md"
                  className="mt-1 w-25"
                  onClick={handleSave}
                >
                  Save
                </Button>
              </Col>
            </Row>
          </Card.Body>
        </Card>
      ) : null}
    </>
  );
};

export default UserPharmacyNotification;
